import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../i18n/translations';
import { api } from '../services/api';
import { isProductionFrontend, MODULE_VERSION } from '../version';
import { canWith, DEFAULT_MOCK_ROLE, MOCK_ROLE_STORAGE_KEY } from '../utils/rbacPermissions';

const LANG_KEY = 'spectra-rrhh-lang';
const THEME_KEY = 'spectra-rrhh-theme';

const AppContext = createContext(null);

function getByPath(obj, path) {
  return path.split('.').reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

export function AppProvider({ children }) {
  const prodUi = isProductionFrontend();
  const [locale, setLocaleState] = useState(() => localStorage.getItem(LANG_KEY) || 'es');
  const [theme, setThemeState] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const [mockRole, setMockRoleState] = useState(() => localStorage.getItem(MOCK_ROLE_STORAGE_KEY) || DEFAULT_MOCK_ROLE);
  const [permSummary, setPermSummary] = useState(null);
  const [authMe, setAuthMe] = useState(null);
  const [authError, setAuthError] = useState(null);

  const refreshAuth = useCallback(() => {
    return api
      .get('/api/auth/me')
      .then((res) => {
        setAuthMe(res.data);
        setAuthError(null);
        return res.data;
      })
      .catch((err) => {
        setAuthMe(null);
        setAuthError(err?.response?.status || 'error');
        return null;
      });
  }, []);

  const refreshPermissions = useCallback(() => {
    const params = prodUi ? undefined : { mock_role: localStorage.getItem(MOCK_ROLE_STORAGE_KEY) || DEFAULT_MOCK_ROLE };
    return api
      .get('/api/permissions/summary', params ? { params } : undefined)
      .then((res) => setPermSummary(res.data))
      .catch(() => setPermSummary(null));
  }, [prodUi]);

  useEffect(() => {
    refreshAuth().then(() => refreshPermissions());
  }, [refreshAuth, refreshPermissions, mockRole]);

  const setMockRole = useCallback(
    (role) => {
      if (prodUi) return;
      const r = role || DEFAULT_MOCK_ROLE;
      localStorage.setItem(MOCK_ROLE_STORAGE_KEY, r);
      setMockRoleState(r);
    },
    [prodUi]
  );

  const canView = useCallback((module) => canWith(permSummary, module, 'view', prodUi), [permSummary, prodUi]);
  const canCreate = useCallback((module) => canWith(permSummary, module, 'create', prodUi), [permSummary, prodUi]);
  const canEdit = useCallback((module) => canWith(permSummary, module, 'edit', prodUi), [permSummary, prodUi]);
  const canApprove = useCallback((module) => canWith(permSummary, module, 'approve', prodUi), [permSummary, prodUi]);
  const canDelete = useCallback((module) => canWith(permSummary, module, 'delete', prodUi), [permSummary, prodUi]);
  const canReject = useCallback((module) => canWith(permSummary, module, 'reject', prodUi), [permSummary, prodUi]);
  const canExport = useCallback((module) => canWith(permSummary, module, 'export', prodUi), [permSummary, prodUi]);
  const canAdmin = useCallback((module) => canWith(permSummary, module, 'admin', prodUi), [permSummary, prodUi]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const setLocale = useCallback((lng) => {
    setLocaleState(lng);
    localStorage.setItem(LANG_KEY, lng);
  }, []);

  const setTheme = useCallback((t) => {
    setThemeState(t);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const t = useCallback(
    (path) => {
      const val = getByPath(translations[locale] || translations.es, path);
      if (val !== undefined) return val;
      return getByPath(translations.es, path) ?? path;
    },
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      theme,
      setTheme,
      toggleTheme,
      t,
      mockRole,
      setMockRole,
      permSummary,
      refreshPermissions,
      authMe,
      authError,
      refreshAuth,
      moduleVersion: MODULE_VERSION,
      isProductionUi: prodUi,
      canView,
      canCreate,
      canEdit,
      canApprove,
      canReject,
      canDelete,
      canExport,
      canAdmin,
    }),
    [
      locale,
      setLocale,
      theme,
      setTheme,
      toggleTheme,
      t,
      mockRole,
      setMockRole,
      permSummary,
      refreshPermissions,
      authMe,
      authError,
      refreshAuth,
      prodUi,
      canView,
      canCreate,
      canEdit,
      canApprove,
      canReject,
      canDelete,
      canExport,
      canAdmin,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
